package xim.poc.game.configuration.v0.tactics

import xim.poc.MobSkillToBlueMagicOverride
import xim.poc.game.*
import xim.poc.game.CombatStat.*
import xim.poc.game.GameEngine.displayName
import xim.poc.game.configuration.SkillApplier
import xim.poc.game.configuration.SkillApplierHelper
import xim.poc.game.configuration.SkillApplierHelper.TargetEvaluator.Companion.compose
import xim.poc.game.configuration.SkillApplierHelper.TargetEvaluator.Companion.noop
import xim.poc.game.configuration.SkillRangeInfo
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.*
import xim.poc.game.configuration.v0.V0MobSkillDefinitions.sourceCurrentHpDamage
import xim.poc.game.configuration.v0.V0MobSkillDefinitions.staticDamage
import xim.poc.game.configuration.v0.V0SpellDefinitions.mndHealingMagic
import xim.poc.game.configuration.v0.V0SpellDefinitions.sourcePercentageHealingMagic
import xim.poc.game.configuration.v0.V0SpellDefinitions.targetPercentageHealingMagic
import xim.poc.game.configuration.v0.tactics.behaviors.AvatarConfig
import xim.poc.game.configuration.v0.tactics.behaviors.BecomeAvatarEvent
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsBehaviorController
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsMetusBehavior
import xim.poc.game.event.*
import xim.poc.ui.ShiftJis
import xim.resource.*
import xim.resource.table.SpellInfoTable.toSpellInfo
import xim.resource.table.StatusEffectNameTable
import xim.util.PI_f
import xim.util.interpolate
import xim.util.toTruncatedString
import kotlin.math.floor
import kotlin.math.roundToInt

class CrossJobSkill(val points: Int, val link: SkillId? = null)
private val crossJobEnabled = CrossJobSkill(points = 0)

class TacticsSkillConfig(
    val minRange: Int,
    val maxRange: Int = minRange,
    val primaryTargetFlag: Int? = null,
    val aoeRange: Int = 0,
    val aoeType: AoeType = AoeType.None,
    val aoeTargetFlag: Int? = null,
    val cost: V0AbilityCost,
    val charges: Int? = null,
    val description: String,
    val skillApplier: SkillApplier,
    val tpDamage: Int? = null,
    val knockback: Boolean = false,
    val drawIn: Boolean = false,
    val crossJob: CrossJobSkill? = null,
    val overwrites: SkillId? = null,
) {
    val rangeInfo: SkillRangeInfo

    init {
        rangeInfo = if (aoeType == AoeType.Line) {
            SkillRangeInfo(maxTargetDistance = 1f + 4f * aoeRange, effectRadius = 1f, type = aoeType)
        } else {
            val range = if (aoeType == AoeType.Cone) { 4.9f } else { 4.1f }
            SkillRangeInfo(maxTargetDistance = 100f, effectRadius = aoeRange * range, type = aoeType, coneAngle = PI_f/4.1f)
        }
    }

    fun isCrossJob() = crossJob != null
}

object TacticsSkills {

    private val configuration = HashMap<SkillId, TacticsSkillConfig>()

    init {
        configuration += banishMagic()
        configuration += banishgaMagic()

        configuration += diaMagic()
        configuration += diagaMagic()

        configuration += bioMagic()
        configuration += poisonMagic()

        configuration += cureMagic()
        configuration += curagaMagic()

        configuration += elementalMagic()
        configuration += elementalAoeMagic()
        configuration += elementalDotMagic()

        configuration += protectraMagic()
        configuration += shellraMagic()

        configuration += ancientMagic()

        configuration += paeon()
        configuration += regenMagic()

        summonMagic()

        miscSongs()
        configuration += requiemMagic()
        configuration += minnes()

        configuration[skillBoost_551] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Boost: 1.5x power on next attack.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Boost, 1) {
                it.status.potency = 50
            }),
        )

        configuration[spellStoneskin_54] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Stoneskin: 20% of target's Max HP.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Stoneskin, 0) {
                it.status.counter = (it.context.targetState.getMaxHp() * 0.20f).roundToInt()
            }),
            crossJob = crossJobEnabled
        )

        configuration[spellSlow_56] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Slow (5 turns): +10% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Slow, duration = 5, potency = 10)))),
            crossJob = crossJobEnabled
        )

        configuration[spellSlowga_357] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Slow (5 turns): +10% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Slow, duration = 5, potency = 10)))),
            crossJob = crossJobEnabled
        )

        configuration[spellSlowII_79] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Slow (5 turns): +15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Slow, duration = 5, potency = 15)))),
            crossJob = crossJobEnabled,
            overwrites = spellSlow_56,
        )

        configuration[spellParalyze_58] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Paralysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20)))),
            crossJob = crossJobEnabled
        )

        configuration[spellParalyga_356] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Paralysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20)))),
            crossJob = crossJobEnabled
        )

        configuration[spellParalyzeII_80] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Paralysis (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 25)))),
            crossJob = crossJobEnabled,
            overwrites = spellParalyze_58,
        )

        configuration[spellHaste_57] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Haste (5 turns): -15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 15 }),
            crossJob = crossJobEnabled
        )

        configuration[spellHastega_358] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Haste (5 turns): -15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 15 }),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = crossJobEnabled
        )

        configuration[spellHasteII_511] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Haste (5 turns): -20% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 20 }),
            crossJob = crossJobEnabled,
            overwrites = spellHaste_57,
        )

        configuration[spellSilence_59] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Silence (3 turns)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Silence, duration = 3)))),
            crossJob = crossJobEnabled
        )

        configuration[spellBlind_254] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Blind (5 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Blind, duration = 5, potency = 20)))),
            crossJob = crossJobEnabled
        )

        configuration[spellBlindga_361] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Blind (5 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Blind, duration = 5, potency = 20)))),
            crossJob = crossJobEnabled
        )

        configuration[spellBlindII_276] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Blind (5 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Blind, duration = 5, potency = 25)))),
            crossJob = crossJobEnabled
        )

        configuration[spellSleep_253] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            tpDamage = 100,
            description = "TP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
        )

        configuration[spellRepose_98] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            tpDamage = 100,
            description = "TP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
        )

        configuration[spellSleepII_259] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            tpDamage = 150,
            description = "TP Damage: 150",
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
            overwrites = spellSleep_253
        )

        configuration[spellRefresh_109] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Grants +300TP to ally.",
            skillApplier = SkillApplier(targetEvaluator = grantTp(300)),
            tpDamage = -300,
            crossJob = crossJobEnabled
        )

        configuration[spellErase_143] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Removes 1 debuff.",
            skillApplier = SkillApplier(targetEvaluator = expireTargetDebuffs(maxToExpire = 1)),
            crossJob = crossJobEnabled
        )

        configuration[spellDrain_245] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.0f }),
            crossJob = crossJobEnabled
        )

        configuration[spellDrainII_246] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.3 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.3f }),
            crossJob = crossJobEnabled,
            overwrites = spellDrain_245
        )

        configuration[spellDrainIII_880] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.5f }),
            crossJob = crossJobEnabled,
            overwrites = spellDrainII_246
        )

        configuration[spellTractor_264] = TacticsSkillConfig(
            minRange = 2,
            maxRange = 4,
            cost = tpCost(300),
            description = "Warps an ally to an adjacent space.",
            skillApplier = SkillApplier(targetEvaluator = noop()),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = crossJobEnabled
        )

        configuration[spellDispel_260] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Dispels 1 buff.",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = AttackEffects(dispelCount = 1))),
            crossJob = crossJobEnabled
        )

        configuration[spellAnimusAugeo_308] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Enmity Boost (5 turns): +80% enmity",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.EnmityBoost, 5) { it.status.potency = 80 }),
            crossJob = crossJobEnabled
        )

        configuration[spellAnimusMinuo_309] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Enmity Down (5 turns): -50% enmity",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.EnmityDown, 5) { it.status.potency = 50 }),
            crossJob = crossJobEnabled
        )

        configuration[spellDispelga_360] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Dispels 1 buff.",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = AttackEffects(dispelCount = 1))),
            crossJob = crossJobEnabled
        )

        configuration[spellBind_258] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Bind (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Bind, duration = 1)))),
        )

        configuration[spellBindga_362] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(600),
            description = "Bind (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Bind, duration = 1)))),
        )

        configuration[spellBreakga_365] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 200",
            tpDamage = 200,
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 1.5f }),
        )

        configuration[spellGravity_216] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Weight (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Weight, duration = 1, potency = 1)))),
        )

        configuration[spellGraviga_366] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Weight (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Weight, duration = 1, potency = 1)))),
        )

        configuration[spellDeath_367] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 3.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 3.0f }),
        )

        configuration[spellCrusade_476] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Enmity Boost (8 turns): +80% enmity",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.EnmityBoost, 8) { it.status.potency = 80 }),
            crossJob = crossJobEnabled
        )

        configuration[spellImpact_503] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nAttributes Down (2 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20, duration = 2))
            ) { 2f }),
            crossJob = crossJobEnabled
        )

        configuration[spellNatMeditation_700] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +25%\nMagic Atk. Boost (5 turns): +25%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 25 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 25 },
            )),
        )

        configuration[spellFullCure_893] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            charges = 2,
            cost = tpCost(400),
            description = "Restores 100% of target's HP.\nRemoves all debuffs.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                targetPercentageHealingMagic(percent = 100f),
                expireTargetDebuffs(maxToExpire = 999),
            )),
            crossJob = crossJobEnabled
        )

        rabbit()
        coeurl()
        tiger()

        mandragora()
        sabotender()
        leafkin()

        leech()
        hecteyes()
        slug()

        bee()
        beetle()
        ladybug()
        chapuli()

        lizard()
        eft()
        raptor()

        bats()
        apkallu()
        tulfaire()

        puk()
        petWyvern()
        dragon()

        crab()
        poroggo()
        seaMonk()

        imp()
        ahriman()
        gargouille()

        pixie()
        heartwing()
        baelfyr()

        spriggan()

        bztavian()
        mammet()
        shantottoClone()
        shantottoII()
        ironclad()
        rampart()
        gears()
        chariot()
        disjoined()
        selhteus()
        shinryu()
        promathia2()
        carbuncle()
        fenrir()
    }

    private fun rabbit() {
        configuration[mskillFootKick_1] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            crossJob = CrossJobSkill(points = 1, link = spellFootKick_577),
        )

        configuration[mskillDustCloud_2] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (DEX)\nBlind (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit, attackEffects = basicStatus(StatusEffect.Blind) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.0f }),
        )

        configuration[mskillWhirlClaws_3] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillWildCarrot_67] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: 0.5 (MND)",
            skillApplier = SkillApplier(targetEvaluator = mndHealingMagic(potency = 0.5f)),
            crossJob = CrossJobSkill(points = 3, link = spellWildCarrot_578),
        )

        configuration[mskillSnowCloud_405] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (STR)\nParalysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit, attackEffects = basicStatus(StatusEffect.Paralysis) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.5f }),
        )

        configuration[mskillWildGinseng_1106] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            charges = 4,
            description = "Regen (5 turns): +10% of user's Max HP per tick\nHaste (5 turns): -20% TP Cost\nAttack Boost (5 turns): +25%\nMagic Atk. Boost (5 turns): +25%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Regen, 5) { it.status.potency = (it.context.sourceState.getMaxHp() * 0.20f).roundToInt() },
                status(StatusEffect.Haste, 5) { it.status.potency = 25 },
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 25 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 25 },
            )),
        )
    }

    private fun tiger() {
        configuration[mskillRoar_14] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (DEX)\nParalysis (3 turn): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit, attackEffects = basicStatus(StatusEffect.Paralysis) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.5f }),
        )

        configuration[mskillRazorFang_15] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
        )

        configuration[mskillClawCyclone_17] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
            crossJob = CrossJobSkill(points = 2),
        )

        configuration[mskillPredatoryGlare_1424] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit magic attack. Power: 1.5 (DEX)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit) { 1.5f }),
            crossJob = CrossJobSkill(points = 3)
        )

        configuration[mskillCrossthrash_1425] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Two-hit attack. Power: 1.25 (STR)\nDispels 1 buff\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 2 }, ftpSpread = true,
                attackEffects = AttackEffects(dispelCount = 1),
            ) { 1.25f }),
            knockback = true,
        )

        configuration[mskillDisorientingWaul_1951] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (DEX)\nMagic Atk. Down (3 turn): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit, attackEffects = basicStatus(StatusEffect.MagicAtkDown) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.8f }),
        )
    }

    private fun mandragora() {
        configuration[mskillHeadButt_44] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            tpDamage = 100,
            description = "Single-hit attack. Power: 1.5 (STR)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillDreamFlower_45] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "TP Damage: 150",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
            crossJob = CrossJobSkill(points = 4, link = spellDreamFlower_678)
        )

        configuration[mskillWildOats_46] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.5 (STR)\nDefense Down (3 turns): 15%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(attackEffects = basicStatus(StatusEffect.DefenseDown2) {
                it.statusState.counter = 3
                it.statusState.potency = 15
            }) { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spellWildOats_603)
        )

        configuration[mskillPhotosynthesis_48] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            charges = 4,
            description = "Regen (5 turns): +10% of user's Max HP per tick",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Regen, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * 0.10f).roundToInt()
            }),
        )

        configuration[mskillLeafDagger_49] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.5 (STR)\nPoison (3 turns). Tick power: 0.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, attackStat = str, defendStat = vit, potency = 0.5f, duration = 3))
            ) { 1.5f })
        )

        configuration[mskillScream_50] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nMagic Def. Down (3 turns): 15%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.MagicDefDown) {
                it.statusState.counter = 3
                it.statusState.potency = 15
            }) { 1.5f }),
        )

        configuration[mskillPetalPirouette_1954] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 150",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillDemonicFlower_2154] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.0${ShiftJis.longTilde}4.0 (INT)\nConsumes 50% of current HP.",
            skillApplier = SkillApplier(
                targetEvaluator = magicalWs { 1f.interpolate(4f, it.ftpScope.attacker.getHpp()) },
                additionalSelfEvaluator = sourceCurrentHpDamage(percent = 0.50f, damageType = AttackDamageType.Static),
            ),
        )

        configuration[mskillFatalScream_2131] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Poison (5 turns): inflicts 10% of user's max HP per tick.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Poison, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * 0.1f).roundToInt()
            }),
        )

        configuration[mskillPhaeosynthesis_2323] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(500),
            charges = 4,
            description = "Regen (5 turns): +15% of user's Max HP\nEvasion Boost (5 turns) +20%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Regen, 5) { it.status.potency = (it.context.sourceState.getMaxHp() * 0.15f).roundToInt() },
                status(StatusEffect.EvasionBoost, 5) { it.status.potency = 20 }
            ) ),
        )
    }

    private fun leech() {
        configuration[mskillAbsorbingKiss_70] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.8f }),
        )

        configuration[mskillSuction_158] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillAcidMist_159] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nAttack Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.AttackDown2) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.0f }),
        )

        configuration[mskillSandBreath_160] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nBlind (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.Blind) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 1.0f }),
        )

        configuration[mskillDrainkiss_161] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.3 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.3f }),
        )

        configuration[mskillRegeneration_162] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            charges = 4,
            description = "Regen (5 turns): +15% of user's Max HP per tick.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Regen, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * 0.1f).roundToInt()
            }),
            crossJob = CrossJobSkill(points = 1),
        )

        configuration[mskillBrainDrain_167] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nMagic Atk. Down (3 turns): 15%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.MagicAtkDown, duration = 3, potency = 15))
            ) { 1.5f }),
            crossJob = CrossJobSkill(points = 2)
        )

        configuration[mskillCaliginosity_2681] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nBio (3 turns): 20%\nBlind (3 turns): 20%\nMagic Atk. Down: 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(attackStatusEffects = listOf(
                spellDamageDoT(StatusEffect.Bio, duration = 3, potency = 0.33f, secondaryPotency = 0.8f),
                staticStatus(statusEffect = StatusEffect.Blind, potency = 20, duration = 3),
                staticStatus(statusEffect = StatusEffect.MagicAtkDown, potency = 20, duration = 3),
            ))) { 2f }),
        )
    }

    private fun bee() {
        configuration[mskillSharpSting_78] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillPollen_79] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(400),
            charges = 4,
            description = "Healing power: 0.5 (MND)",
            skillApplier = SkillApplier(targetEvaluator = mndHealingMagic(potency = 0.5f)),
        )
    }

    private fun beetle() {
        configuration[mskillPowerAttack_82] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillHiFreqField_83] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nInhibit TP (3 turns): 50%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.InhibitTP, duration = 3, potency = 50))
            ) { 1.5f }),
            crossJob = CrossJobSkill(points = 2),
        )

        configuration[mskillRhinoAttack_84] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nKnockback: 1",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
            knockback = true,
        )

        configuration[mskillRhinoGuard_85] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Defense Boost (5 turns): -15% phys. dmg. taken\nMagic Def. Boost (5 turns): -15% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 15 },
                status(StatusEffect.DefenseBoost, 5) { it.status.potency = 15 },
            )),
            crossJob = CrossJobSkill(points = 1),
        )

        configuration[mskillSpoil_87] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nAttack Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 1.0f }),
        )

        configuration[mskillRhinowrecker_2567] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)\nDefense Down (3 turns): 20%\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.DefenseDown2, duration = 3, potency = 20))
            ) { 2.0f }),
            knockback = true,
            crossJob = CrossJobSkill(points = 4, link = spellRhinowrecker_760)
        )
    }

    private fun lizard() {
        configuration[mskillTailBlow_110] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit attack. Power: 1.8 (STR)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
        )

        configuration[mskillFireball_111] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillBlockhead_112] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            knockback = true,
        )

        configuration[mskillBrainCrush_113] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(450),
            description = "Single-hit attack. Power: 2.0 (STR)\nSilence (2 turn)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(attackEffects = basicStatus(StatusEffect.Silence) {
                it.statusState.counter = 2
            }) { 2.0f }),
        )

        configuration[mskillPlaguebreath_115] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nPoison (3 turns). Tick power: 0.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.5f, duration = 3))
            ) { 1.5f })
        )

        configuration[mskillInfrasonics_116] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Inhibit TP (3 turns): 50%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(
                attackEffects = single(staticStatus(StatusEffect.InhibitTP, potency = 50, duration = 5))
            )),
            crossJob = CrossJobSkill(points = 1, link = spellInfrasonics_610)
        )

        configuration[mskillSecretion_117] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Evasion Boost (5 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = status(statusEffect = StatusEffect.EvasionBoost, counter = 5) {
                it.status.potency = 20
            }),
            crossJob = CrossJobSkill(points = 1),
        )

        configuration[mskillSnowball_365] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
            crossJob = CrossJobSkill(points = 2),
        )
    }

    private fun bats() {
        configuration[mskillUltrasonics_136] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.DefenseDown2, duration = 3, potency = 20))
            ) { 1.0f }),
        )

        configuration[mskillSonicBoom_137] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nAttack Down (3 turns): 20%.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 1.0f }),
            crossJob = CrossJobSkill(points = 1),
        )

        configuration[mskillBloodDrain_138] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.5f }),
        )

        configuration[mskillJetStream_139] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Three-hit attack. Power: 0.55 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 3 }, ftpSpread = true) { 0.55f }),
        )

        configuration[mskillSlipstream_901] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBlind (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 3, potency = 20))
            ) { 1.5f }),
        )

        configuration[mskillTurbulence_902] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit) { 1.5f }),
        )

        configuration[mskillKnifeEdgeCircle_1085] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(600),
            description = "Single-hit attack. Power: 2.0 (STR)\nPoison (3 turns).",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.33f, duration = 3))
            ) { 2.0f })
        )

        configuration[mskillTrainFall_1086] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit, attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 2.0f }),
        )
    }

    private fun hecteyes() {
        configuration[mskillDeathRay_181] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
            crossJob = CrossJobSkill(points = 2, link = spellDeathRay_522),
        )

        configuration[mskillHexEye_182] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nParalysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20))
            ) { 1.5f }),
        )

        configuration[mskillPetroGaze_183] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillCatharsis_184] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            charges = 4,
            description = "Healing power: 30% of Max HP",
            skillApplier = SkillApplier(targetEvaluator = sourcePercentageHealingMagic(30f)),
            crossJob = CrossJobSkill(points = 1, link = spellCatharsis_761)
        )
    }

    private fun crab() {
        configuration[mskillBubbleShower_186] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.0 (INT)\nAttack Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 1.0f }),
        )

        configuration[mskillBubbleCurtain_187] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Magic Def. Boost (5 turns): -15% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 15 }),
        )

        configuration[mskillBigScissors_188] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillScissorGuard_189] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Defense Boost (5 turns): -15% phys. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.DefenseBoost, 5) { it.status.potency = 15 }),
        )

        configuration[mskillMetallicBody_192] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Stoneskin: 20% Max HP.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Stoneskin, 0) {
                it.status.counter = (it.context.sourceState.getMaxHp() * 0.20f).roundToInt()
            }),
            crossJob = CrossJobSkill(points = 1, link = spellMetallicBody_517),
        )

        configuration[mskillVenomShower_2256] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.DefenseDown2) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 2.0f }),
        )

        configuration[mskillMegaScissors_2257] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Cone,
            aoeRange = 2,
            cost = tpCost(450),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f }),
        )
    }

    private fun seaMonk() {
        configuration[mskillTentacle_200] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit) { 1.8f }),
        )

        configuration[mskillInkJet_202] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBlind (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 3, potency = 25))
            ) { 1.5f }),
        )

        configuration[mskillHardMembrane_203] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(400),
            description = "Evasion Boost (5 turns): 20%\nDefense Boost (5 turns): -20% phys. dmg. taken\nMagic Def. Boost (5 turns): -20% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.EvasionBoost, 5) { it.status.potency = 20 },
                status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 20 },
                status(StatusEffect.DefenseBoost, 5) { it.status.potency = 20 },
            )),
            crossJob = CrossJobSkill(points = 3)
        )

        configuration[mskillCrossAttack_204] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Two-hit attack. Power: 1.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 2 }, ftpSpread = true) { 1.0f }),
        )

        configuration[mskillMaelstrom_206] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nAttack Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 2.0f }),
            crossJob = CrossJobSkill(points = 4, link = spellMaelstrom_515)
        )

        configuration[mskillWhirlwind_207] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.DefenseDown2, duration = 3, potency = 20))
            ) { 2.0f }),
        )
    }

    private fun coeurl() {
        configuration[mskillPetrifactiveBreath_224] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            tpDamage = 100,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillFrenziedRage_225] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +20%",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.AttackBoost2, 5) {
                it.status.potency = 20
            }),
        )

        configuration[mskillPounce_226] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillChargedWhisker_227] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (DEX)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit) { 2f }),
            crossJob = CrossJobSkill(points = 3, link = spellChargedWhisker_680)
        )

        configuration[mskillBlaster_396] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nParalysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20))
            ) { 1.5f }),
        )

        configuration[mskillChaoticEye_397] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (DEX)\nMagic Atk. Down (3 turn): -20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = dex, defendStat = vit, attackEffects = single(
                staticStatus(StatusEffect.MagicAtkDown, potency = 20, duration = 3)
            )) { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spellChaoticEye_582)
        )

        configuration[mskillMortalBlast_2346] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(800),
            description = "Single-hit magic attack. Power: 3.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 3.0f }),
        )

        configuration[mskillPreternaturalGleam_2504] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (MND)\nBio (3 turns): -10% STR\nParalysis (3 turns): 25%\nSilence (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd, attackEffects = AttackEffects(attackStatusEffects = listOf(
                spellDamageDoT(StatusEffect.Bio, duration = 3, potency = 0.33f, secondaryPotency = 0.0f),
                staticStatus(statusEffect = StatusEffect.Paralysis, potency = 20, duration = 3),
                staticStatus(statusEffect = StatusEffect.Silence, potency = 25, duration = 3),
            ))) { 2f }),
        )
    }

    private fun ahriman() {
        configuration[mskillBlindeye_292] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)\nBlind (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 3, potency = 25))
            ) { 1.5f }),
        )

        configuration[mskillEyesOnMe_293] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(450),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
            crossJob = CrossJobSkill(points = 2, link = spellEyesOnMe_557)
        )

        configuration[mskillHypnosis_294] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 150,
            description = "TP Damage: 150",
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
        )

        configuration[mskillMindBreak_295] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nMagic Atk. Down (3 turn): -20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = single(
                staticStatus(StatusEffect.MagicAtkDown, potency = 20, duration = 3)
            )) { 1.5f }),
        )

        configuration[mskillBindingWave_296] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nBind (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Bind, duration = 1))
            ) { 1.8f }),
        )

        configuration[mskillAiryShield_297] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Evasion Boost (5 turns): 20%.",
            skillApplier = SkillApplier(targetEvaluator = status(statusEffect = StatusEffect.EvasionBoost, counter = 5) {
                it.status.potency = 20
            }),
        )

        configuration[mskillMagicBarrier_299] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            description = "Magic Def. Boost (5 turns): -20% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = status(statusEffect = StatusEffect.MagicDefBoost, counter = 5) {
                it.status.potency = 20
            }),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 2, link = spellMagicBarrier_668)
        )

        configuration[mskillDeathlyGlare_2512] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.5f }),
        )
    }

    private fun dragon() {
        configuration[mskillFlameBreath_386] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
        )

        configuration[mskillPoisonBreath_387] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nPoison (3 turns). Tick power: 0.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.5f, duration = 3))
            ) { 1.5f })
        )

        configuration[mskillWindBreath_388] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
            knockback = true,
        )

        configuration[mskillBodySlam_389] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f }),
            crossJob = CrossJobSkill(points = 3, link = spellBodySlam_564)
        )

        configuration[mskillHeavyStomp_390] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)\nParalysis (3 turns): 20%\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20))
            ) { 2.0f }),
            knockback = true,
        )

        configuration[mskillChaosBlade_391] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nCurse (3 turns): -10% Max HP",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(statusEffect = StatusEffect.Curse, potency = 10, duration = 3))
            ) { 2.0f })
        )

        configuration[mskillPetroEyes_392] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillDiscordantNote_3507] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(800),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nWeight (2 turns)\nTP Damage: 200\nKnockback",
            tpDamage = 200,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, potency = 1, duration = 2))
            ) { 2f }),
            knockback = true,
        )
    }

    private fun imp() {
        configuration[mskillAbrasiveTantara_1453] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            tpDamage = 100,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillBugleCall_1456] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBio (3 turns): -10% STR. Tick power: 0.3 (INT)\nSelf: Haste (3 turns): -15% TP Cost",
            skillApplier = SkillApplier(
                targetEvaluator = magicalWs(attackEffects = single(spellDamageDoT(StatusEffect.Bio, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))) { 1.5f },
                additionalSelfEvaluator = status(StatusEffect.Haste, 3) { it.status.potency = 15 }
            ),
        )

        configuration[mskillDeafeningTantara_1454] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nMagic Atk. Down (3 turn): -20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = single(
                staticStatus(StatusEffect.MagicAtkDown, potency = 20, duration = 3)
            )) { 1.5f }),
        )

        configuration[mskillFreneticRip_1455] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Three-hit attack. Power: 0.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 3 }, ftpSpread = true) { 0.5f }),
            crossJob = CrossJobSkill(points = 1, link = spellFreneticRip_560)
        )

        configuration[mskillGratingTantara_1747] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nTP Damage: 200",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

        configuration[mskillStiflingTantara_1748] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nMagic Atk. Down (3 turn): -20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = single(
                staticStatus(StatusEffect.MagicAtkDown, potency = 20, duration = 3)
            )) { 2.0f }),
        )

        configuration[mskillDarkRecital_2632] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nParalysis (3 turns): 20%\nDispels 2 buffs",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(statusEffect = StatusEffect.Paralysis, dispelCount = 2) {
                it.statusState.counter = 3
                it.statusState.potency = 20
            }) { 2.0f }),
        )
    }

    private fun apkallu() {
        configuration[mskillYawn_1457] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "TP Damage: 150",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = tpDamageOnly()),
            crossJob = CrossJobSkill(points = 4, link = spellYawn_576)
        )

        configuration[mskillWingSlap_1458] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Four-hit attack. Power: 0.45 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 4 }, ftpSpread = true) { 0.45f }),
        )

        configuration[mskillBeakLunge_1459] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            knockback = true,
        )

        configuration[mskillFrigidShuffle_1460] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (AGI)\nParalysis (3 turn): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = agi, defendStat = vit,
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 1, potency = 20))
            ) { 1.5f }),
        )

        configuration[mskillWingWhirl_1461] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit) { 1.8f }),
        )

        configuration[mskillWhiteout_2563] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 150",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
        )
    }

    private fun puk() {
        configuration[mskillCrosswind_1462] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillWindShear_1464] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

        configuration[mskillObfuscate_1465] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBlind (1 turn): 35%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.Blind) {
                it.statusState.counter = 1
                it.statusState.potency = 35
            }) { 1.5f }),
        )

        configuration[mskillZephyrMantle_1466] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Blink: 2",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Blink, 0) {
                it.status.counter = 2
            }),
            crossJob = CrossJobSkill(points = 2),
        )

        configuration[mskillIllWind_1467] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nDispels 1 buff",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                dispelCount = 1,
            )) { 1.5f }),
        )

        configuration[mskillWhiteWind_1468] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(600),
            charges = 4,
            description = "Healing power: 30% of user's Max HP",
            skillApplier = SkillApplier(targetEvaluator = sourcePercentageHealingMagic(30f)),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 3, link = spellWhiteWind_690),
        )

        configuration[mskillSomnialDurance_2388] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(700),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 200\nChoke (3 turns): VIT -10%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Choke, potency = 0.33f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.0f })
        )
    }

    private fun poroggo() {
        configuration[mskillMagicHammer_1702] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spellMagicHammer_646)
        )

        configuration[mskillWaterBomb_1703] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(450),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nSilence (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Silence, duration = 1))
            ) { 1.8f }),
            crossJob = CrossJobSkill(points = 3, link = spellWaterBomb_687)
        )

        configuration[mskillFrogCheer_1704] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Magic Atk. Boost (5 turns): +20%",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.MagicAtkBoost, 5) {
                it.status.potency = 20
            }),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 2),
        )

        configuration[mskillProvidence_1705] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Gain \"Spontaneity\" (1 charge)",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Spontaneity, 1)),
        )

        configuration[mskillFrogChorus_2652] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nPoison (3 turns). Tick power: 0.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.5f, duration = 3))
            ) { 1.5f })
        )

        configuration[mskillUsurpingScepter_2633] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.5f }),
        )
    }

    private fun slug() {
        configuration[mskillFuscousOoze_1927] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nWeight (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, duration = 1, potency = 1))
            ) { 1.8f })
        )

        configuration[mskillPurulentOoze_1928] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nBio (3 turns): -10% STR. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Bio, duration = 3, potency = 0.3f, secondaryPotency = 0.9f))
            ) { 1.8f }),
        )

        configuration[mskillCorrosiveOoze_1929] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nAttack Down (3 turns): 20%\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(attackStatusEffects = listOf(
                    staticStatus(statusEffect = StatusEffect.AttackDown2, potency = 20, duration = 3),
                    staticStatus(statusEffect = StatusEffect.DefenseDown2, potency = 20, duration = 3),
                )),
            ) { 2.0f }),
            crossJob = CrossJobSkill(points = 3, link = spellCorrosiveOoze_651)
        )

        configuration[mskillMucilaginousOoze_1930] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(450),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nSlow (3 turns): +15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Slow, duration = 3, potency = 15))
            ) { 2.0f }),
        )
    }

    private fun pixie() {
        configuration[mskillZephyrArrow_1937] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.5 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            knockback = true,
        )

        configuration[mskillLetheArrows_1938] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 1.8 (STR)\nBind (1 turn)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Bind, duration = 1)),
            ) { 1.8f }),
            knockback = true,
        )

        configuration[mskillSpringBreeze_1939] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (MND)\nTP Damage: 100.",
            tpDamage = 100,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(mnd) { 1.5f }),
        )

        configuration[mskillSummerBreeze_1940] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Haste (5 turns): -15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 15 }),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 3),
        )

        configuration[mskillAutumnBreeze_1941] = TacticsSkillConfig(
            minRange = 0,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: 0.5 (MND)\nRemoves 1 debuff.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                mndHealingMagic(potency = 0.5f),
                expireTargetDebuffs(maxToExpire = 1),
            )),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 3, link = spellAutumnBreeze_528),
        )

        configuration[mskillWinterBreeze_1942] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDispels 1 buff.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                dispelCount = 1
            )) { 2.9f }),
        )

        configuration[mskillCyclonicTurmoil_1943] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillCyclonicTorrent_1944] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(450),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nMagic Atk. Down (3 turns): 20%\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.MagicAtkDown, duration = 3, potency = 20)),
            ) { 2.0f })
        )

        configuration[mskillNornArrows_2262] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
        )

        configuration[mskillCyclonicBlight_2438] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
            tpDamage = 200,
        )

        configuration[mskillEldritchWind_2566] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDispels 3 buffs.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(dispelCount = 3)
            ) { 2.0f }),
        )

    }

    private fun bztavian() {
        configuration[mskillMandibularLashing_2746] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Three-hit attack. Power: 0.7 (STR)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 3 }, ftpSpread = true) { 0.7f }),
        )

        configuration[mskillVespineHurricane_2747] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nAttack Down (3 turns): 20%\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 2.0f }),
        )

        configuration[mskillStingerVolley_2748] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 2.0 (STR)\nParalysis (3 turns): 30%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 30))
            ) { 2.0f }),
        )

        configuration[mskillDroningWhirlwind_2749] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeType = AoeType.Source,
            aoeRange = 8,
            cost = tpCost(1000),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDispels all buffs\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(dispelCount = 10)
            ) { 2f }),
        )

        configuration[mskillIncisiveDenouement_2750] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillIncisiveApotheosis_2751] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2f }),
        )

    }

    private fun tulfaire() {
        configuration[mskillPentapeck_2808] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Five-hit attack. Power: 0.4 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 5 }, ftpSpread = true) { 0.4f }),
        )

        configuration[mskillSwoopingFrenzy_2809] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 3,
            aoeType = AoeType.Line,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)\nDefense Down (3 turns): 15%\nMagic Def. Down (3 turns): 15%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = AttackEffects(attackStatusEffects = listOf(
                    staticStatus(statusEffect = StatusEffect.DefenseDown2, potency = 15, duration = 3),
                    staticStatus(statusEffect = StatusEffect.MagicDefDown, potency = 15, duration = 3),
                )),
            ) { 2.0f }),
            crossJob = CrossJobSkill(points = 4),
        )

        configuration[mskillMoltingPlumage_2807] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Cone,
            aoeRange = 2,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (AGI)\nDispels 1 buff\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = agi, defendStat = vit, attackEffects = AttackEffects(
                dispelCount = 1,
            )) { 2f }),
        )

        configuration[mskillFromtheSkies_2810] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 3,
            aoeType = AoeType.Line,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.3 (AGI)\nChoke (3 turns): VIT -10%. Tick power: 0.3 (AGI)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = agi, defendStat = vit,
                attackEffects = single(spellDamageDoT(StatusEffect.Choke, attackStat = agi, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.3f }),
        )

        configuration[mskill_2811] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(700),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 200\nFrost (3 turns): AGI -10%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Frost, potency = 0.33f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.0f })
        )
    }

    private fun petWyvern() {
        configuration[mskillHealingBreath_638] = TacticsSkillConfig(
            minRange = 1,
            primaryTargetFlag = TargetFlag.Ally.flag,
            aoeType = AoeType.Cone,
            aoeRange = 2,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: 0.5 (MND)\nRemoves 1 debuff.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                mndHealingMagic(potency = 0.5f),
                expireTargetDebuffs(maxToExpire = 1),
            )),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillHealingBreathII_639] = TacticsSkillConfig(
            minRange = 1,
            primaryTargetFlag = TargetFlag.Ally.flag,
            aoeType = AoeType.Cone,
            aoeRange = 2,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: 0.6 (MND)\nRemoves 1 debuff.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                mndHealingMagic(potency = 0.6f),
                expireTargetDebuffs(maxToExpire = 1),
            )),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            overwrites = mskillHealingBreath_638,
        )

        configuration[mskillHealingBreathIII_640] = TacticsSkillConfig(
            minRange = 1,
            primaryTargetFlag = TargetFlag.Ally.flag,
            aoeType = AoeType.Cone,
            aoeRange = 2,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: 0.8 (MND)\nRemoves 2 debuffs.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                mndHealingMagic(potency = 0.8f),
                expireTargetDebuffs(maxToExpire = 2),
            )),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 4),
            overwrites = mskillHealingBreathII_639,
        )

        for (i in mskillFlameBreath_644.id .. mskillHydroBreath_649.id) {
            configuration[MobSkillId(i)] = TacticsSkillConfig(
                minRange = 1,
                aoeRange = 2,
                aoeType = AoeType.Cone,
                cost = tpCost(400),
                description = "Single-hit magic attack. Power: 1.65 (INT)",
                skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.65f }),
            )
        }
    }

    private fun heartwing() {
        configuration[mskillAncestralBanishment_2727] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (MND)\nDispels 1 buff",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd, defendStat = mnd, attackEffects = AttackEffects(
                dispelCount = 1,
            )) { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spellAncestralBanishment_763)
        )

        configuration[mskillHeartfeltAura_2728] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(400),
            charges = 4,
            description = "Regen (5 turns): +10% of user's Max HP per tick\nDefense Boost (5 turns): -15% phys. dmg. taken\nMagic Def. Boost (5 turns): -15% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Regen, 5) { it.status.potency = (it.context.sourceState.getMaxHp() * 0.10f).roundToInt() },
                status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 15 },
                status(StatusEffect.DefenseBoost, 5) { it.status.potency = 15 },
            )),
            crossJob = CrossJobSkill(points = 2)
        )

        configuration[mskillImpairingGlister_2729] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (MND)\nDia (5 turns): -10% VIT. Tick power: 0.2 (MND)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd, defendStat = mnd,
                attackEffects = single(spellDamageDoT(StatusEffect.Dia, attackStat = mnd, potency = 0.2f, secondaryPotency = 0.9f, duration = 3))
            ) { 1.5f }),
        )

        configuration[mskillCripplingGleam_2730] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(800),
            description = "Single-hit magic attack. Power: 2.0 (MND)\nAttributes Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20))
            ) { 2f }),
        )

    }

    private fun baelfyr() {
        configuration[mskillSearingTempest_2479] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nBurn (3 turns): STR -10%. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Burn, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.0f }),
            crossJob = CrossJobSkill(points = 3, link = spellSearingTempest_719)
        )

        configuration[mskillBlindingFulgor_2480] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (MND)\nBlind (1 turn): 35%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd,
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 1, potency = 35))
            ) { 2f }),
            crossJob = CrossJobSkill(points = 3, link = spellBlindingFulgor_725)
        )
    }

    private fun sabotender() {
        configuration[mskillNeedleshot_65] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskill1000Needles_66] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spell1000Needles_595)
        )

        configuration[mskillPhotosynthesis_68] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            charges = 4,
            description = "Regen (5 turns): +10% of user's Max HP per tick",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Regen, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * 0.1f).roundToInt()
            }),
        )

        configuration[mskill2000Needles_1369] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2f }),
        )

        configuration[mskillChupaBlossom_2505] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 2.0f }),
        )

        configuration[mskill4000Needles_1370] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit attack. Power: 2.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.5f }),
        )

        configuration[mskill10000Needles_864] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(800),
            description = "Single-hit attack. Power: 3.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 3f }),
        )

    }

    private fun leafkin() {
        configuration[mskillConsecration_2722] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            tpDamage = 100,
            description = "Single-hit attack. Power: 1.5 (STR)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillSacredCaper_2723] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (MND)\nSlow (3 turns): +15% TP Cost\nRasp (3 turns): DEX -10%. Tick power: 0.3 (MND)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd,
                attackEffects = AttackEffects(attackStatusEffects = listOf(
                    spellDamageDoT(statusEffect = StatusEffect.Rasp, attackStat = mnd, defendStat = mnd, potency = 0.3f, secondaryPotency = 0.9f, duration = 3),
                    staticStatus(statusEffect = StatusEffect.Slow, duration = 3, potency = 15),
                )),
            ) { 1.5f })
        )

        configuration[mskillPhototrophicBlessing_2724] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            charges = 4,
            description = "Regen (5 turns): +10% of user's Max HP per tick\nDefense Boost (5 turns): -15% phys. dmg. taken\nMagic Def. Boost (5 turns): -15% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Regen, 5) { it.status.potency = (it.context.sourceState.getMaxHp() * 0.10f).roundToInt() },
                status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 15 },
                status(StatusEffect.DefenseBoost, 5) { it.status.potency = 15 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillPhototrophicWrath_2725] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 3,
            cost = tpCost(400),
            description = "Haste (5 turns): -15% TP Cost\nAttack Boost (5 turns): +20%\nMagic Atk. Boost (5 turns): +20%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Haste, 5) { it.status.potency = 15 },
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 20 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 20 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 3)
        )

        configuration[mskillDeificGambol_2726] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(800),
            description = "Single-hit magic attack. Power: 2.5 (MND)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd) { 2.5f }),
        )

    }

    private fun ladybug() {
        configuration[mskillSuddenLunge_1922] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            tpDamage = 100,
            description = "Single-hit attack. Power: 1.5 (STR)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
            crossJob = CrossJobSkill(points = 2, link = spellSuddenLunge_692)
        )

        configuration[mskillNoisomePowder_1923] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nAttack Down (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 25))
            ) { 1.5f }),
        )

        configuration[mskillNepentheanHum_1924] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            tpDamage = 100,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillSpiralSpin_1925] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(450),
            description = "Single-hit attack. Power: 1.8 (STR)\nBlind (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 3, potency = 25))
            ) { 1.8f }),
            crossJob = CrossJobSkill(points = 2, link = spellSpiralSpin_652)
        )

        configuration[mskillSpiralBurst_1926] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nErase up to 3 debuffs from self",
            skillApplier = SkillApplier(
                targetEvaluator = magicalWs(attackStat = str, defendStat = vit) { 2f },
                additionalSelfEvaluator = expireTargetDebuffs(3)
            ),
        )

        configuration[mskillSearingEffulgence_2677] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nDia (3 turns): -10% VIT\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(spellDamageDoT(StatusEffect.Dia, potency = 0.33f, secondaryPotency = 0.9f, duration = 3))
            ) { 2f }),
            knockback = true,
        )
    }

    private fun chapuli() {
        configuration[mskillSensillaBlades_2690] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 3,
            aoeType = AoeType.Line,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
            knockback = true,
        )

        configuration[mskillSanguinarySlash_2692] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit) { 1.5f }),
            knockback = true,
            crossJob = CrossJobSkill(points = 2, link = spellSanguinarySlash_550)
        )

        configuration[mskillTegminaBuffet_2691] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.2 (STR)\nChoke (3 turns): VIT -10%. Tick power: 0.3 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Choke, attackStat = str, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.2f })
        )

        configuration[mskillNaturesMeditation_2689] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +25%\nMagic Atk. Boost (5 turns): +25%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 25 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 25 },
            )),
            crossJob = CrossJobSkill(points = 2, link = spellNatMeditation_700)
        )

        configuration[mskillOrthopterror_2693] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(600),
            tpDamage = 100,
            description = "Single-hit attack. Power: 2.5 (STR)\nTP Damage: 100\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.5f }),
            knockback = true,
        )

        configuration[mskillShockiavona_2967] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.2 (STR)\nShock (3 turns): MND -10%. Tick power: 0.3 (STR)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(spellDamageDoT(StatusEffect.Shock, attackStat = str, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.2f })
        )
    }

    private fun eft() {
        configuration[mskillToxicSpit_259] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nPoison (5 turns). Tick power: 0.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.5f, duration = 5))
            ) { 1.5f })
        )

        configuration[mskillGeistWall_260] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(300),
            description = "Dispels 1 buff",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = AttackEffects(dispelCount = 1))),
            crossJob = CrossJobSkill(points = 1, link = spellGeistWall_605),
        )

        configuration[mskillNumbingNoise_261] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 150",
            tpDamage = 150,
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillNimbleSnap_262] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Three-hit attack. Power: 0.6 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 3 }, ftpSpread = true) { 0.6f }),
        )

        configuration[mskillCyclotail_263] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(450),
            description = "Single-hit attack. Power: 1.8 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
            knockback = true
        )

        configuration[mskillTarichutoxin_2680] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (STR)\nPoison (5 turns)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 0.33f, duration = 5))
            ) { 2.0f }),
            knockback = true
        )
    }

    private fun raptor() {
        configuration[mskillRipperFang_118] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
        )

        configuration[mskillFoulBreath_120] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

        configuration[mskillFrostBreath_121] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(450),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nParalysis (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 20))
            ) { 1.8f }),
            crossJob = CrossJobSkill(points = 3, link = spellFrostBreath_608)
        )

        configuration[mskillThunderbolt_122] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
            tpDamage = 100,
        )

        configuration[mskillChompRush_123] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nSlow (3 turns): +15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Slow, duration = 3, potency = 15))
            ) { 1.8f }),
        )

        configuration[mskillScytheTail_124] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.2 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.2f }),
            knockback = true,
        )

        configuration[mskillWhirlingInferno_2787] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.2 (INT)\nAttack Down (3 turns): 20%\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(attackStatusEffects = listOf(
                    staticStatus(statusEffect = StatusEffect.AttackDown2, potency = 20, duration = 3),
                    staticStatus(statusEffect = StatusEffect.DefenseDown2, potency = 20, duration = 3),
                )),
            ) { 2.2f }),
        )

    }

    private fun gargouille() {
        configuration[mskillDarkOrb_2165] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
            crossJob = CrossJobSkill(points = 2, link = spellDarkOrb_689)
        )

        configuration[mskillDarkMist_2166] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nWeight (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, potency = 1, duration = 1))
            ) { 1.8f }),
        )

        configuration[mskillTriumphantRoar_2167] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +20%",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.AttackBoost2, 5) {
                it.status.potency = 20
            }),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = CrossJobSkill(points = 2, link = spellTriumphantRoar_655)
        )

        configuration[mskillTerrorEye_2168] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit magic attack. Power: 1.5 (INT)\nTP Damage: 100",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillBloodyClaw_2169] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 1.8f }),
        )

        configuration[mskillShadowBurst_2170] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nMagic Def. Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(statusEffect = StatusEffect.MagicDefDown, potency = 20, duration = 3))
            ) { 2.0f })
        )
    }

    private fun spriggan() {
        configuration[mskillJitteringJig_2888] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 3,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +20%\nMagic Atk. Boost (5 turns): +20%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 15 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 15 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillRomp_2889] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )

        configuration[mskillFreneticFlurry_2890] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Eight-hit attack. Power: 0.2 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 8 }, ftpSpread = true) { 0.2f }),
        )
    }

    private fun mammet() {
        configuration[mskillVelociousBlade_91] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Five-hit attack. Power: 0.3 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 5 }, ftpSpread = true) { 0.3f }),
        )

        configuration[mskillScissionThrust_163] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.4 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.4f }),
        )

        configuration[mskillSonicBlade_166] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.4 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.4f }),
        )

        configuration[mskillMicroquake_185] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f })
        )

        configuration[mskillPercussiveFoin_191] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.6 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.6f }),
        )

        configuration[mskillGravityWheel_201] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 1.6 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, duration = 1, potency = 1))
            ) { 1.6f }),
        )

        configuration[mskillPsychomancy_208] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 0.5 (INT)\nAbsorbs 50% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.5f)
            )) { 0.5f }),
        )

        configuration[mskillMindWall_215] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Magic Def. Boost (3 turns): -50% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 50 }),
        )

        configuration[mskillTransmogrification_231] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "???",
            skillApplier = SkillApplier(targetEvaluator = noop()),
        )
    }

    private fun shantottoClone() {
        configuration[skillFullSwing_182] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
        )

        configuration[skillRetribution_184] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.5f }),
        )
    }

    private fun shantottoII() {
        configuration[mskillFinalExam_3484] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(bypassShadows = true),
            ) { 2.0f }),
        )

        configuration[mskillDoctorsOrders_3485] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = AttackEffects(bypassShadows = true),
            ) { 2.0f }),
        )

        configuration[mskillEmpiricalResearch_3486] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            aoeRange = 4,
            aoeType = AoeType.Line,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 3.0 (INT)\nMagic Def. Down (3 turns): 50%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(statusEffect = StatusEffect.MagicDefDown, potency = 50, duration = 3))
            ) { 3.0f })
        )

        configuration[mskillLessoninPain_3487] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 2.0 (STR)\nDefense Down (3 turns): 25%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = AttackEffects(bypassShadows = true, attackStatusEffects = listOf(staticStatus(statusEffect = StatusEffect.DefenseDown2, potency = 25, duration = 3)))
            ) { 2.0f }),
        )
    }

    private fun ironclad() {
        configuration[mskillTurbineCyclone_2363] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nKnockback",
            knockback = true,
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
        )

        configuration[mskillSeismicImpact_2364] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            tpDamage = 100,
            description = "Single-hit magic attack. Power: 1.8 (STR)\nTP Damage: 100\nSlow (3 turns): +15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = str, defendStat = vit,
                attackEffects = single(staticStatus(StatusEffect.Slow, duration = 3, potency = 15)),
            ) { 1.5f }),
        )

        configuration[mskillIncinerator_2365] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nBurn (3 turns): STR -20%. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Burn, potency = 0.3f, secondaryPotency = 0.8f, duration = 3))
            ) { 1.8f }),
        )

        configuration[mskillArmCannon_2366] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

        configuration[mskillBallisticKick_2367] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 4,
            aoeType = AoeType.Line,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f })
        )

        configuration[mskillScapulaBeam_2368] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nAttributes Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20))
            ) { 2f }),
        )

        configuration[mskillEradicator_2369] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(600),
            description = "Single-hit magic attack. Power: 3.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 3f }),
        )

        configuration[mskillIncinerator_2475] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)\nFrost (3 turns): AGI -20%. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Frost, potency = 0.3f, secondaryPotency = 0.8f, duration = 3))
            ) { 1.8f }),
        )

        configuration[mskillArmCannon_2476] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

        configuration[mskillBallisticKick_2477] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 4,
            aoeType = AoeType.Line,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f })
        )
    }

    private fun rampart() {
        configuration[mskillRollerChain_1776] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBind (1 turn)\nWeight (2 turns)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(attackStatusEffects = listOf(
                staticStatus(statusEffect = StatusEffect.Bind, duration = 1),
                staticStatus(statusEffect = StatusEffect.Weight, potency = 1, duration = 2),
            ))) { 1.5f }),
        )

        configuration[mskillChokeChain_1777] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nBind (1 turn)\nSilence (2 turns)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(attackStatusEffects = listOf(
                staticStatus(statusEffect = StatusEffect.Bind, duration = 1),
                staticStatus(statusEffect = StatusEffect.Silence, duration = 2),
            ))) { 1.5f }),
        )

        configuration[mskillReinforcements_1778] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(500),
            description = "???",
            skillApplier = SkillApplier(targetEvaluator = noop())
        )

        configuration[mskillBiomagnet_1779] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 3,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 1.8 (STR)\nDraw-in",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 1.8f }),
            drawIn = true,
        )

        configuration[mskillAstralGate_1780] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 2.0 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f }),
            knockback = true,
        )

        configuration[mskillWarp_1781] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Cone,
            cost = tpCost(500),
            description = "???",
            skillApplier = SkillApplier(targetEvaluator = staticDamage(999_999))
        )
    }

    private fun gears() {
        configuration[mskillArtificialGravity_1782] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nWeight (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, potency = 1, duration = 1))
            ) { 1.5f }),
        )

        configuration[mskillAntigravity_1783] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.5 (INT)\nKnockback (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(StatusEffect.Weight, potency = 1, duration = 1))
            ) { 1.5f }),
        )

        configuration[mskillRailCannon_1784] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 1.8 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.8f }),
        )

    }

    private fun chariot() {
        configuration[mskillDiffusionRay_1798] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 2,
            aoeType = AoeType.Cone,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (MND)\nDispels 1 buff",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd, attackEffects = AttackEffects(
                dispelCount = 1,
            )) { 2.0f }),
        )

        configuration[mskillInertiaStream_1799] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit attack. Power: 2.2 (STR)\nBind (1 turn)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Bind, duration = 1))
            ) { 2.2f }),
        )

        configuration[mskillDischarge_1800] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nParalysis (3 turn): 30%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = basicStatus(StatusEffect.Paralysis) {
                it.statusState.counter = 3
                it.statusState.potency = 30
            }) { 2f }),
        )

        configuration[mskillHomingMissile_1802] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Reduces target HP by 90%",
            skillApplier = SkillApplier(targetEvaluator = hppAttack(hpp = 0.9f)),
        )

        configuration[mskillDiscoid_1803] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit magic attack.\nPower: 15.0 (INT), split between targets.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 15f / it.context.allTargetStates.size }),
        )
    }

    private fun disjoined() {
        configuration[skillRequiescat_226] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Five-hit attack. Power: 0.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(numHits = { 5 }, ftpSpread = true) { 0.5f }),
        )

        configuration[skillUrielBlade_238] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.8 (MND)\nBlind (1 turn): 35%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd, attackEffects = basicStatus(StatusEffect.Blind) {
                it.statusState.counter = 1
                it.statusState.potency = 35
            }) { 1.8f }),
        )

        configuration[skillGlorySlash_239] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            tpDamage = 100,
            description = "Single-hit attack. Power: 2.0 (STR)\nParalysis (1 turn): 20%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(attackEffects = basicStatus(StatusEffect.Paralysis) {
                it.statusState.counter = 1
                it.statusState.potency = 20
            }) { 2.0f }),
        )

        configuration[skillUnbridledLearning_810] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Allows access to additional blue magic.",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.UnbridledLearning, 1)),
        )

        configuration[skillUnbridledWisdom_850] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(300),
            description = "Allows access to additional blue magic.\nReduces TP Cost of blue magic by 30%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.UnbridledLearning, 3),
                status(StatusEffect.UnbridledWisdom, 3) { it.status.potency = 30 },
            )),
        )

        configuration[spellSearingTempest_719] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nBurn (3 turns): STR -10%. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(spellDamageDoT(StatusEffect.Burn, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { 2.0f }),
        )

        configuration[spellSpectralFloe_720] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 2.0f }),
        )

        configuration[spellAnvilLightning_721] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 2.0f }),
        )

        configuration[spellEntomb_722] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 2.0 (INT)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 2.0f }),
        )

        configuration[spellBlindingFulgor_725] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (MND)\nBlind (1 turn): 35%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackStat = mnd,
                attackEffects = single(staticStatus(StatusEffect.Blind, duration = 1, potency = 35))
            ) { 2f }),
        )

        configuration[spellScouringSpate_726] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nAttack Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(staticStatus(StatusEffect.AttackDown2, duration = 3, potency = 20))
            ) { 2.0f }),
        )

        configuration[spellSilentStorm_727] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 2.0 (STR)\nSilence (2 turn)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackEffects = basicStatus(StatusEffect.Silence) {
                it.statusState.counter = 2
            }) { 2.0f }),
        )

        configuration[spellTenebralCrush_728] = TacticsSkillConfig(
            minRange = 1,
            aoeRange = 1,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 2.0 (INT)\nDefense Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(staticStatus(StatusEffect.DefenseDown2, duration = 3, potency = 20))
            ) { 2.0f }),
        )

        configuration[spellThunderbolt_736] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            tpDamage = 200,
            description = "Single-hit magic attack. Power: 5.0 (INT)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { 5.0f }),
        )

        configuration[spellGatesofHades_739] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 5.0 (INT)\nBurn (3 turns): STR -20%. Tick power: 0.6 (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(spellDamageDoT(StatusEffect.Burn, potency = 0.6f, secondaryPotency = 0.8f, duration = 3))
            ) { 5.0f }),
        )

        configuration[spellTourbillion_740] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 5.0 (STR)\nDefense Down (3 turns): 30%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackStat = str, defendStat = vit,
                attackEffects = single(staticStatus(StatusEffect.DefenseDown2, duration = 3, potency = 30))
            ) { 5.0f }),
        )

        configuration[spellCesspool_752] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 5.0 (STR)\nSlow (3 turns): 30%",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(staticStatus(StatusEffect.Slow, duration = 3, potency = 30))
            ) { 5.0f }),
        )

        configuration[spellUproot_747] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 5.0 (MND)\nErase all debuffs from self",
            skillApplier = SkillApplier(
                targetEvaluator = magicalWs(attackStat = mnd) { 5.0f },
                additionalSelfEvaluator = expireTargetDebuffs(999)
            ),
        )

        configuration[spellMightyGuard_750] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(500),
            description = "Haste (5 turns) -25% TP Cost\nRegen (5 turns): +10% of user's Max HP per tick\nDefense Boost (5 turns): -30% phys. dmg. taken\nMagic Def. Boost (5 turns): -30% mag. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Haste, 5) { it.status.potency = 25 },
                status(StatusEffect.Regen, 5) { it.status.potency = (it.context.sourceState.getMaxHp() * 0.10f).roundToInt() },
                status(StatusEffect.MagicDefBoost, 5) { it.status.potency = 30 },
                status(StatusEffect.DefenseBoost, 5) { it.status.potency = 30 },
            )),
        )

        configuration[mskillFrizz_2871] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 1.5 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 1.5f }),
        )

        configuration[mskillMegaflare_1295] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(1000),
            description = "Single-hit magic attack. Power: 6.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 6.0f }),
        )

    }

    private fun selhteus() {
        configuration[mskillLuminousLance_3365] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 2.0 (MND)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd) { 2.0f }),
        )

        configuration[mskillRejuvenation_3366] = TacticsSkillConfig(
            minRange = 0,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeType = AoeType.Source,
            aoeRange = 8,
            cost = tpCost(500),
            charges = 1,
            description = "Healing power: 100% of user's Max HP.\nErase all debuffs.",
            skillApplier = SkillApplier(targetEvaluator = compose(
                sourcePercentageHealingMagic(100f),
                expireTargetDebuffs(maxToExpire = 999),
            )),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillRevelation_3367] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            cost = tpCost(400),
            description = "Four-hit attack. Power: 0.5 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 2.0f }),
        )

    }

    private fun shinryu() {
        val skills = listOf(
            mskillCataclysmicVortex_2409,
            mskillCosmicBreath_2408,
            mskillCataclysmicVortex_2409,
            mskillGyreCharge_2410,
            mskillMightyGuard_2411,
            mskillAtomicRay_2412,
            mskillDarkMatter_2413,
            mskillProtostar_2414,
            mskillSupernova_2415,
        )

        for (skill in skills) {
            configuration[skill] = TacticsSkillConfig(
                minRange = 1,
                maxRange = 6,
                cost = tpCost(300),
                tpDamage = 100,
                description = "Single-hit attack. Power: 1.5 (STR)\nTP Damage: 100",
                skillApplier = SkillApplier(targetEvaluator = physicalWs { 0.1f }),
            )
        }

    }

    private fun promathia2() {
        configuration[TacticsMetusBehavior.autoAttackSkill] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(0),
            description = "Single-hit attack. Power: 3.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 3.0f })
        )

        configuration[mskillMalevolentBlessing_1244] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 5.0 (INT)\nCurse (3 turns): -25% Max HP",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(staticStatus(statusEffect = StatusEffect.Curse, potency = 25, duration = 3))
            ) { 4.0f })
        )

        configuration[mskillPestilentPenance_1245] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 4,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 4.0 (INT)\nPoison (3 turns). Tick power: 1.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = 1f, duration = 3))
            ) { 4.0f })
        )

        configuration[mskillEmptySalvation_1246] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 4.0 (STR)\nDispels 1 buff",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(attackEffects = AttackEffects(
                dispelCount = 1,
            )) { 4.0f }),
        )

        configuration[mskillInfernalDeliverance_1247] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit attack. Power: 4.0 (STR)\nTP Damage: 100",
            tpDamage= 100,
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 4.0f }),
        )

        configuration[mskillWheelofImpregnability_1248] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(0),
            description = "Negates physical damage.",
            skillApplier = SkillApplier(targetEvaluator = noop()),
        )

        configuration[mskillBastionofTwilight_1249] = TacticsSkillConfig(
            minRange = 0,
            cost = tpCost(0),
            description = "Negates magical damage.",
            skillApplier = SkillApplier(targetEvaluator = noop()),
        )

        configuration[mskillWindsofOblivion_1250] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 9,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            description = "Amnesia (3 turns)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Amnesia, duration = 2)))),
        )

        configuration[mskillSealofQuiescence_1251] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 9,
            aoeRange = 2,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            description = "Silence (3 turns)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Silence, duration = 2)))),
        )

        configuration[mskillCensure_3489] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 4.0 (INT)\nAttributes Down (2 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20, duration = 2))
            ) { 4.0f }),
        )

        configuration[mskillOsmoticWave_3490] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 8,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 4.0 (INT)\nAbsorbs 25% of damage dealt.",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                absorptionEffect = AttackAbsorptionEffect(absorption = 0.25f)
            )) { 4.0f }),
        )
    }

    private fun miscSongs() {
        configuration[spellSheepfoeMambo_403] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Evasion Boost (5 turns): 15%",
            skillApplier = SkillApplier(targetEvaluator = status(statusEffect = StatusEffect.EvasionBoost, counter = 5) {
                it.status.potency = 20
            }),
        )

        configuration[spellDragonfoeMambo_404] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Evasion Boost (5 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = status(statusEffect = StatusEffect.EvasionBoost, counter = 5) {
                it.status.potency = 20
            }),
            overwrites = spellSheepfoeMambo_403
        )

        configuration[spellAdvancingMarch_419] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Haste (5 turns): -10% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 10 }),
            crossJob = crossJobEnabled
        )

        configuration[spellVictoryMarch_420] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Haste (5 turns): -15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Haste, 5) { it.status.potency = 15 }),
            crossJob = crossJobEnabled
        )

        configuration[spellBattlefieldElegy_421] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Slow (3 turns): +10% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Slow, duration = 5, potency = 10)))),
            crossJob = crossJobEnabled
        )

        configuration[spellCarnageElegy_422] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Slow (3 turns): +15% TP Cost",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(staticStatus(StatusEffect.Slow, duration = 5, potency = 15)))),
            crossJob = crossJobEnabled,
            overwrites = spellBattlefieldElegy_421
        )

        configuration[spellFoeSirvente_468] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(500),
            description = "Enmity Boost (5 turns): +80% enmity",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.EnmityBoost, 5) { it.status.potency = 80 }),
            crossJob = crossJobEnabled
        )

        configuration[spellAdventurersDirge_469] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(500),
            description = "Enmity Down (5 turns): -50% enmity",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.EnmityDown, 5) { it.status.potency = 50 }),
            crossJob = crossJobEnabled
        )

        configuration[spellRaptorMazurka_467] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 1,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Flee (2 turns): Move +1 (Max 3)",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Flee, 2) { it.status.potency = 1 }),
            crossJob = crossJobEnabled
        )

        configuration[spellChocoboMazurka_465] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            description = "Flee (3 turns): Move +1 (Max 3)",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Flee, 2) { it.status.potency = 1 }),
            crossJob = crossJobEnabled,
            overwrites = spellRaptorMazurka_467
        )
    }

    private fun carbuncle() {
        configuration[mskillPoisonNails_651] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 2.0 (STR)\nPoison (3 turns). Tick power: 1.0 (STR)",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(spellDamageDoT(StatusEffect.Poison, attackStat = str, defendStat = vit, potency = 1f, duration = 3))
            ) { 2f })
        )

        configuration[mskillShiningRuby_652] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            charges = 4,
            description = "Protect (5 turns): -50% physical damage taken\nShell (5 turns): -50% magical damage taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Protect, 5) { it.status.potency = 50 },
                status(StatusEffect.Shell, 5) { it.status.potency = 50 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillGlitteringRuby_653] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Attack Boost (5 turns): +50%\nMagic Atk. Boost (5 turns): +50%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 50 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 50 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillMeteorite_654] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: 2.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 2.0f }),
        )

        configuration[mskillHealingRubyII_655] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(600),
            charges = 1,
            description = "Healing power: 30% of user's Max HP",
            skillApplier = SkillApplier(targetEvaluator = sourcePercentageHealingMagic(30f)),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillSearingLight_656] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 6.0 (MND)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackStat = mnd) { 6f }),
        )

        configuration[mskillHolyMist_3297] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 3.0 (MND)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackStat = mnd) { 3f }),
        )
    }

    private fun fenrir() {
        configuration[mskillMoonlitCharge_575] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeRange = 3,
            aoeType = AoeType.Line,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 3.0 (STR)\nKnockback",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 3f }),
            knockback = true,
        )

        configuration[mskillCrescentFang_576] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            description = "Single-hit attack. Power: 4.0 (STR)\nParalysis (3 turns): 50%",
            skillApplier = SkillApplier(targetEvaluator = physicalWs(
                attackEffects = single(staticStatus(StatusEffect.Paralysis, duration = 3, potency = 50))
            ) { 4.0f }),
        )

        configuration[mskillEclipseBite_580] = TacticsSkillConfig(
            minRange = 1,
            cost = tpCost(300),
            tpDamage = 200,
            description = "Single-hit attack. Power: 4.0 (STR)\nTP Damage: 200",
            skillApplier = SkillApplier(targetEvaluator = physicalWs { 4.0f }),
        )

        configuration[mskillLunarCry_577] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(0),
            description = "Attributes Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20, duration = 3)
            ))),
        )

        configuration[mskillEclipticGrowl_578] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            charges = 4,
            description = "Protect (5 turns): -50% physical damage taken\nShell (5 turns): -50% magical damage taken",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.Protect, 5) { it.status.potency = 50 },
                status(StatusEffect.Shell, 5) { it.status.potency = 50 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillEclipticHowl_581] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeRange = 2,
            aoeType = AoeType.Source,
            cost = tpCost(300),
            description = "Attack Boost (5 turns): +50%\nMagic Atk. Boost (5 turns): +50%",
            skillApplier = SkillApplier(targetEvaluator = compose(
                status(StatusEffect.AttackBoost2, 5) { it.status.potency = 50 },
                status(StatusEffect.MagicAtkBoost, 5) { it.status.potency = 50 },
            )),
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
        )

        configuration[mskillLunarRoar_579] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 2,
            cost = tpCost(0),
            description = "Dispels 2 buffs",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = AttackEffects(dispelCount = 2))),
        )

        configuration[mskillHowlingMoon_582] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 8,
            aoeType = AoeType.Source,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 6.0 (INT)",
            skillApplier = SkillApplier(targetEvaluator = magicalWs { 6f }),
        )

        configuration[mskillLunarBay_3295] = TacticsSkillConfig(
            minRange = 1,
            aoeType = AoeType.Source,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 4.0 (INT)\nAttributes Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20))
            ) { 4f }),
        )

        configuration[mskillImpact_3296] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(500),
            description = "Single-hit magic attack. Power: 4.0 (INT)\nAttributes Down (3 turns): 20%",
            skillApplier = SkillApplier(targetEvaluator = magicalWs(attackEffects = AttackEffects(
                attackStatusEffects = allStatsDown(potency = 20))
            ) { 4f }),
        )

    }

    private fun summonMagic() {
        for (config in AvatarConfig.values()) {
            configuration[config.summonSkill] = TacticsSkillConfig(
                minRange = 0,
                cost = tpCost(500),
                description = "???",
                skillApplier = SkillApplier(targetEvaluator = { listOf(BecomeAvatarEvent(config, it)) }),
            )

            configuration[config.astralFlow] = TacticsSkillConfig(
                minRange = 1,
                maxRange = 2,
                aoeRange = 8,
                aoeType = AoeType.Source,
                cost = tpCost(500),
                description = "Single-hit magic attack. Power: 6.0 (INT)",
                skillApplier = SkillApplier(targetEvaluator = magicalWs { 6f }),
            )
        }
    }

    operator fun get(skillId: SkillId) = configuration[skillId] ?: throw IllegalStateException("Not defined: ${skillId.displayName()}")

    fun getNullable(skillId: SkillId?) = configuration[skillId]

    fun toOverride(actorState: ActorState, skillId: SkillId): MobSkillToBlueMagicOverride? {
        if (!actorState.isTacticsUnit() || skillId !is MobSkillId) { return null }
        if (actorState.getJobConfig().learnsSkill(skillId)) { return null }
        if (skillId == mskillEald2WarpIn_732 || skillId == mskillEald2WarpOut_733 || skillId == mskillPteraketosAutoAttack_2611) { return null }
        return MobSkillToBlueMagicOverride(movementLockDuration = 0)
    }

}

private fun tpCost(amount: Int): V0AbilityCost {
    return V0AbilityCost(baseCost = AbilityCost(type = AbilityCostType.Tp, value = amount))
}

private fun status(statusEffect: StatusEffect, counter: Int, statusStateSetter: (StatusEffectContext) -> Unit = { }): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        val statusState = StatusEffectState(statusEffect)
        statusState.counter = counter
        statusState.linkedSkillId = it.skill
        statusState.displayCounter = true
        statusStateSetter.invoke(StatusEffectContext(statusState, it))
        listOf(ActorGainStatusEvent(
            sourceId = it.sourceState.id,
            targetId = it.targetState.id,
            statusState = statusState,
            context = it.context,
        ))
    }
}

fun expireTargetDebuffs(maxToExpire: Int): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator { expireDebuffs(maxToExpire, it.targetState) }
}

private fun expireDebuffs(maxToExpire: Int, actorState: ActorState): List<Event> {
    val statuses = actorState.getStatusEffects()
        .filter { it.statusEffect.debuff && (it.canErase || it.canEsuna) }
        .take(maxToExpire)
        .map { it.statusEffect }

    return listOf(ActorExpireStatusEvent(actorState.id, statuses))
}

private fun physicalWs(numHits: (SkillApplierHelper.TargetEvaluatorContext) -> Int = { 1 },
                       ftpSpread: Boolean = false,
                       attackEffects: AttackEffects = AttackEffects(),
                       ftp: TpScalingFn = TpScalingFn { 1f },
): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        val wsResult = WeaponSkillDamageCalculator.physicalWeaponSkill(
            skill = it.skill,
            attacker = it.sourceState,
            defender = it.targetState,
            numHits = numHits.invoke(it),
            ftpSpread = ftpSpread,
            context = it.context,
            ftp = ftp,
        )

        weaponSkillToEvents(
            context = it,
            wsResult = wsResult,
            damageType = AttackDamageType.Physical,
            attackEffects = attackEffects,
        )
    }
}

private fun magicalWs(attackStat: CombatStat = CombatStat.int,
                      defendStat: CombatStat = attackStat,
                      attackEffects: AttackEffects = AttackEffects(),
                      damageCap: Int? = null,
                      potencyFn: PotencyFn = PotencyFn { 1f },
): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {

        val ftp = TpScalingFn { tpContext ->
            potencyFn.invoke(PotencyContext(it, tpContext))
        }

        val wsResult = WeaponSkillDamageCalculator.magicalWeaponSkill(
            skill = it.skill,
            attacker = it.sourceState,
            defender = it.targetState,
            attackStat = attackStat,
            defendStat = defendStat,
            damageCap = damageCap,
            ftp = ftp,
        )

        weaponSkillToEvents(
            context = it,
            wsResult = wsResult,
            damageType = AttackDamageType.Magical,
            attackEffects = attackEffects,
        )
    }
}

private fun weaponSkillToEvents(
    context: SkillApplierHelper.TargetEvaluatorContext,
    wsResult: WeaponSkillDamageResult,
    damageType: AttackDamageType,
    attackEffects: AttackEffects = AttackEffects(),
): List<Event> {
    return listOf(ActorAttackedEvent(
        sourceId = context.sourceState.id,
        targetId = context.targetState.id,
        damageAmount = wsResult.damage,
        damageType = damageType,
        sourceTpGain = 0,
        sourceAdditionalHitTpGain = 0,
        targetTpGain = computeTargetTpGained(context.sourceState, context.targetState),
        targetAdditionalHitTpGain = 0,
        attackEffects = attackEffects,
        skill = context.skill,
        actionContext = context.context,
    ))
}


private fun hppAttack(hpp: Float, attackEffects: AttackEffects = AttackEffects()): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        val damage = floor(it.targetState.getHp() * hpp).roundToInt()
        val wsResult = WeaponSkillDamageResult(damage = listOf(damage), sourceTpGained = 0, targetTpGained = 0)
        weaponSkillToEvents(
            context = it,
            wsResult = wsResult,
            damageType = AttackDamageType.Static,
            attackEffects = attackEffects,
        )
    }
}

private fun offenseSpell(
    attackStat: CombatStat = CombatStat.int,
    defendStat: CombatStat = attackStat,
    numHits: Int = 1,
    attackEffects: AttackEffects = AttackEffects(),
    damageCap: Int? = null,
    potencyFn: SpellPotencyFn,
): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        offenseSpell(
            context = it,
            attackStat = attackStat,
            defendStat = defendStat,
            damageType = AttackDamageType.Magical,
            numHits = numHits,
            attackEffects = attackEffects,
            damageCap = damageCap,
            potencyFn = potencyFn,
        )
    }
}

private fun offenseSpell(
    context: SkillApplierHelper.TargetEvaluatorContext,
    attackStat: CombatStat,
    defendStat: CombatStat = attackStat,
    damageType: AttackDamageType = AttackDamageType.Magical,
    numHits: Int = 1,
    attackEffects: AttackEffects = AttackEffects(),
    damageCap: Int? = null,
    potencyFn: SpellPotencyFn,
): List<Event> {
    return spellResultToEvents(
        context, SpellDamageCalculator.computeDamage(
            potencyFn = potencyFn,
            skill = context.skill,
            attacker = context.sourceState,
            defender = context.targetState,
            originalTarget = context.primaryTargetState,
            attackStat = attackStat,
            defendStat = defendStat,
            numHits = numHits,
            damageCap = damageCap,
            context = context.context,
            damageType = damageType,
        ), attackEffects = attackEffects
    )
}

private fun spellResultToEvents(
    context: SkillApplierHelper.TargetEvaluatorContext,
    spellResult: OffenseMagicResult,
    attackEffects: AttackEffects = AttackEffects(),
): List<Event> {
    val outputEvents = ArrayList<Event>()

    outputEvents += ActorAttackedEvent(
        sourceId = context.sourceState.id,
        targetId = context.targetState.id,
        damageAmount = spellResult.damage,
        damageType = spellResult.damageType,
        sourceTpGain = 0,
        sourceAdditionalHitTpGain = 0,
        targetTpGain = computeTargetTpGained(context.sourceState, context.targetState),
        targetAdditionalHitTpGain = 0,
        attackEffects = attackEffects,
        skill = context.skill,
        actionContext = context.context,
    )

    return outputEvents
}

private fun debuffSpell(attackEffects: AttackEffects): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        listOf(ActorAttackedEvent(
            sourceId = it.sourceState.id,
            targetId = it.targetState.id,
            damageAmount = listOf(0),
            damageType = AttackDamageType.StatusOnly,
            sourceTpGain = 0,
            sourceAdditionalHitTpGain = 0,
            targetTpGain = 0,
            targetAdditionalHitTpGain = 0,
            attackEffects = attackEffects,
            skill = it.skill,
            actionContext = it.context,
        ))
    }
}

private fun basicStatus(statusEffect: StatusEffect, dispelCount: Int = 0, decorator: (AttackStatusEffectContext) -> Unit): AttackEffects {
    return AttackEffects(dispelCount = dispelCount, attackStatusEffects = listOf(
        AttackStatusEffect(statusEffect = statusEffect, baseDuration = null) {
            it.statusState.displayCounter = true
            decorator.invoke(it)
        }
    ))
}

private fun grantTp(amount: Int): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        listOf(ActorHealedEvent(sourceId = it.sourceState.id, targetId = it.targetState.id, amount = amount, actionContext = it.context, healType = ActorResourceType.TP))
    }
}

private fun single(attackStatusEffect: AttackStatusEffect): AttackEffects {
    return AttackEffects(attackStatusEffects = listOf(attackStatusEffect))
}

private fun spellDamageDoT(statusEffect: StatusEffect, potency: Float, duration: Int, attackStat: CombatStat = CombatStat.int, defendStat: CombatStat = attackStat, secondaryPotency: Float = 0f): AttackStatusEffect {
    return AttackStatusEffect(statusEffect = statusEffect, baseDuration = null) {
        val spellResult = SpellDamageCalculator.computeDamage(
            skill = null,
            attacker = it.sourceState,
            defender = it.targetState,
            attackStat = attackStat,
            defendStat = defendStat,
        ) { potency }

        it.statusState.potency = spellResult.damage.firstOrNull() ?: 0
        it.statusState.secondaryPotency = secondaryPotency

        it.statusState.displayCounter = true
        it.statusState.counter = duration
    }
}

private fun staticStatus(statusEffect: StatusEffect, duration: Int, potency: Int = 0, secondaryPotency: Float = 0f): AttackStatusEffect {
    return AttackStatusEffect(statusEffect = statusEffect, baseDuration = null) {

        it.statusState.potency = potency
        it.statusState.secondaryPotency = secondaryPotency

        it.statusState.displayCounter = true
        it.statusState.counter = duration
    }
}

private fun tpDamageOnly(): SkillApplierHelper.TargetEvaluator {
    return SkillApplierHelper.TargetEvaluator {
        listOf(Event {
            val behavior = it.targetState.behaviorController as? TacticsBehaviorController
            behavior?.applyTpDamage(it.skill, it.context)
            emptyList()
        })
    }
}

private fun cureMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellCure_1.id .. spellCureVI_6.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 0.4f + 0.1f * i

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            cost = tpCost(300),
            charges = 4,
            description = "Healing power: ${power.toTruncatedString(1)} (MND)",
            skillApplier = SkillApplier(targetEvaluator = mndHealingMagic(power)),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}

private fun curagaMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellCuraga_7.id .. spellCuragaV_11.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 0.4f + 0.1f * i

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(500),
            charges = 4,
            description = "Healing power: ${power.toTruncatedString(1)} (MND)",
            skillApplier = SkillApplier(targetEvaluator = mndHealingMagic(power)),
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}


private fun banishMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellBanish_28.id .. spellBanishV_32.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val (power, tier) = getElementalMagicPowerAndTier(spell) ?: continue
        val overwrite = if (tier > 1) { SpellSkillId(spell.id - 1) } else { null }

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: ${power.toTruncatedString(2)} (MND)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackStat = mnd) { power }),
            crossJob = crossJobEnabled,
            overwrites = overwrite,
        )
    }

    return definitions
}

private fun banishgaMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellBanishga_38.id .. spellBanishgaV_42.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val (power, tier) = getElementalMagicPowerAndTier(spell) ?: continue
        val overwrite = if (tier > 1) { SpellSkillId(spell.id - 1) } else { null }

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: ${power.toTruncatedString(2)} (MND)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(attackStat = mnd) { power }),
            crossJob = crossJobEnabled,
            overwrites = overwrite,
        )
    }

    return definitions
}

private fun elementalMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellFire_144.id .. spellWaterV_173.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val (power, tier) = getElementalMagicPowerAndTier(spell) ?: continue
        val overwrite = if (tier > 1) { SpellSkillId(spell.id - 1) } else { null }

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "Single-hit magic attack. Power: ${power.toTruncatedString(2)} (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { power }),
            crossJob = crossJobEnabled,
            overwrites = overwrite,
        )
    }

    return definitions
}

private fun elementalAoeMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellFiraga_174.id .. spellWatergaV_203.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val (power, tier) = getElementalMagicPowerAndTier(spell) ?: continue
        val overwrite = if (tier > 1) { SpellSkillId(spell.id - 1) } else { null }

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeType = AoeType.Target,
            aoeRange = 1,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: ${power.toTruncatedString(2)} (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell { power }),
            crossJob = crossJobEnabled,
            overwrites = overwrite,
        )
    }

    return definitions
}

private fun getElementalMagicPowerAndTier(spell: SpellSkillId): Pair<Float, Int>? {
    val parts = spell.displayName().split(" ")
    val tierOffset = when (parts.getOrNull(1) ?: "I") {
        "I" -> 0
        "II" -> 1
        "III" -> 2
        "IV" -> 3
        "V" -> 4
        "VI" -> 5
        else -> return null
    }

    return (1f + 0.2f * tierOffset) to (tierOffset + 1)
}

private fun ancientMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellFlare_204.id .. spellFloodII_215.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val tier = spell.displayName().split(" ").size

        if (tier == 1) {
            definitions[spell] = TacticsSkillConfig(
                minRange = 1,
                maxRange = 3,
                charges = 3,
                cost = tpCost(600),
                description = "Single-hit magic attack. Power: 3.0 (INT)",
                skillApplier = SkillApplier(targetEvaluator = offenseSpell { 3.0f }),
            )
        } else {
            definitions[spell] = TacticsSkillConfig(
                minRange = 1,
                maxRange = 3,
                charges = 3,
                cost = tpCost(600),
                description = "Single-hit magic attack. Power: 3.5 (INT)",
                skillApplier = SkillApplier(targetEvaluator = offenseSpell { 3.5f }),
            )
        }
    }

    return definitions
}

private fun protectraMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellProtectra_125.id .. spellProtectraV_129.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 3,
            aoeRange = 2,
            aoeType = AoeType.Target,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Protect (60 turns): -${10 + i*2}% physical damage taken\n(Automatically applied at start of match)",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Protect, 60) {
                it.status.potency = 10 + i * 2
            }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}

private fun shellraMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellShellra_130.id .. spellShellraV_134.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            maxRange = 3,
            aoeRange = 2,
            aoeType = AoeType.Target,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            description = "Shell (60 turns): -${10 + i*2}% magical damage taken\n(Automatically applied at start of match)",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Shell, 60) {
                it.status.potency = 10 + i * 2
            }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}

private fun diaMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellDia_23.id .. spellDiaV_27.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val potency = 10 + (i * 1.2f).roundToInt()

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(250),
            description = "Single-hit magic attack. Power: 0.5 (MND)\nDia (5 turns): -${potency}% VIT. Tick power: 0.2 (MND)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(spellDamageDoT(StatusEffect.Dia, attackStat = mnd, potency = 0.2f, secondaryPotency = (100 - potency)/100f, duration = 5))
            ) { 0.5f }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i - 1),
        )
    }

    return definitions
}

private fun diagaMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellDiaga_33.id .. spellDiagaV_37.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val potency = 10 + i * 2

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: 0.5 (MND)\nDia (5 turns): -${potency}% VIT. Tick power: 0.2 (MND)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(spellDamageDoT(StatusEffect.Dia, attackStat = mnd, potency = 0.2f, secondaryPotency = (100 - potency)/100f, duration = 5))
            ) { 0.5f }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i - 1),
        )
    }

    return definitions
}

private fun bioMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellBio_230.id .. spellBioV_234.id).map { SpellSkillId(it) }

    for (spell in spells) {
        val (power, tier) = getElementalMagicPowerAndTier(spell) ?: continue
        val overwrite = if (tier > 1) { SpellSkillId(spell.id - 1) } else { null }

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(400),
            description = "Single-hit magic attack. Power: ${power.toTruncatedString(2)} (INT)\nBio (3 turns): -10% STR. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = offenseSpell(
                attackEffects = single(spellDamageDoT(StatusEffect.Bio, potency = 0.3f, secondaryPotency = 0.9f, duration = 3))
            ) { power }),
            crossJob = crossJobEnabled,
            overwrites = overwrite,
        )
    }

    return definitions
}

private fun poisonMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellPoisonga_225.id .. spellPoisongaV_229.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 0.5f + 0.1f * i

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            aoeRange = 1,
            aoeType = AoeType.Target,
            cost = tpCost(300),
            description = "Poison (5 turns). Tick power: ${power.toTruncatedString(1)} (INT)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = power, duration = 5)))),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}

private fun paeon(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellArmysPaeon_378.id .. spellArmysPaeonVIII_385.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 8f + 0.5f * i

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            charges = 4,
            description = "Regen (5 turns): +${power.toTruncatedString(1)}% of user's Max HP per tick",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Regen, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * power/100f).roundToInt()
            }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i - 1),
        )
    }

    return definitions
}

private fun minnes(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellKnightsMinne_389.id .. spellKnightsMinneV_393.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 15 + i

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            aoeType = AoeType.Source,
            aoeRange = 2,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(400),
            charges = 4,
            description = "Defense Boost (5 turns): -${power}% phys. dmg. taken",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.DefenseBoost, 5) { it.status.potency = power }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i - 1),
        )
    }

    return definitions
}

private fun requiemMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = (spellFoeRequiem_368.id .. spellFoeRequiemV_372.id).map { SpellSkillId(it) }

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 0.5f + 0.1f * i

        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 3,
            cost = tpCost(300),
            description = "Poison (5 turns). Tick power: ${power.toTruncatedString(2)} (INT)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(spellDamageDoT(StatusEffect.Poison, potency = power, duration = 5)))),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i-1),
        )
    }

    return definitions
}


private fun regenMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = listOf(spellRegen_108, spellRegenII_110, spellRegenIII_111, spellRegenIV_477, spellRegenV_504)

    for (i in spells.indices) {
        val spell = spells[i]
        val power = 8 + i

        definitions[spell] = TacticsSkillConfig(
            minRange = 0,
            aoeRange = 2,
            aoeType = AoeType.Source,
            primaryTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            aoeTargetFlag = TargetFlag.Self.flag or TargetFlag.Ally.flag,
            cost = tpCost(300),
            charges = 4,
            description = "Regen (5 turns): +${power}% of user's Max HP per tick",
            skillApplier = SkillApplier(targetEvaluator = status(StatusEffect.Regen, 5) {
                it.status.potency = (it.context.sourceState.getMaxHp() * power / 100f).roundToInt()
            }),
            crossJob = crossJobEnabled,
            overwrites = spells.getOrNull(i - 1),
        )
    }

    return definitions
}

private fun elementalDotMagic(): Map<SkillId, TacticsSkillConfig> {
    val definitions = HashMap<SkillId, TacticsSkillConfig>()
    val spells = listOf(
        spellBurn_235 to (StatusEffect.Burn to "INT"),
        spellFrost_236 to (StatusEffect.Frost to "AGI"),
        spellChoke_237 to (StatusEffect.Choke to "VIT"),
        spellRasp_238 to (StatusEffect.Rasp to "DEX"),
        spellShock_239 to (StatusEffect.Shock to "MND"),
        spellDrown_240 to (StatusEffect.Drown to "STR"),
    )

    for ((spell, info) in spells) {
        val (status, stat) = info
        definitions[spell] = TacticsSkillConfig(
            minRange = 1,
            maxRange = 2,
            cost = tpCost(300),
            description = "${StatusEffectNameTable.first(status.id)} (5 turns): $stat -10%. Tick power: 0.3 (INT)",
            skillApplier = SkillApplier(targetEvaluator = debuffSpell(attackEffects = single(spellDamageDoT(status, potency = 0.3f, secondaryPotency = 0.9f, duration = 5)))),
            crossJob = crossJobEnabled,
        )
    }

    return definitions
}

private fun allStatsDown(potency: Int, duration: Int = 3): List<AttackStatusEffect> {
    return listOf(
        staticStatus(statusEffect = StatusEffect.AttackDown2, duration = duration, potency = potency),
        staticStatus(statusEffect = StatusEffect.DefenseDown2, duration = duration, potency = potency),
        staticStatus(statusEffect = StatusEffect.MagicAtkDown, duration = duration, potency = potency),
        staticStatus(statusEffect = StatusEffect.MagicDefDown, duration = duration, potency = potency),
    )
}

fun SkillId.tacticsElement(): SpellElement {
    return when (this) {
        is MobSkillId -> MobSkills[this].element
        is SpellSkillId -> this.toSpellInfo().element
        is AbilitySkillId -> SpellElement.None
        is ItemSkillId -> SpellElement.None
        is RangedAttackSkillId -> SpellElement.None
    }
}

private fun computeTargetTpGained(attacker: ActorState, defender: ActorState): Int {
    val attackerBonuses = CombatBonusAggregator[attacker]

    val agiMitigation = (DamageCalculator.computeAgiRatio(defender, attacker) - 0.5f).coerceIn(0f, 1f)
    val attackerSubtleBlow = attackerBonuses.subtleBlow.toPenaltyMultiplier(0f, 1f)

    val defenderBonuses = CombatBonusAggregator[defender]
    val inhibitTp = defenderBonuses.inhibitTp.toPenaltyMultiplier()

    val rawTpGained = 60f * agiMitigation * attackerSubtleBlow * inhibitTp
    return rawTpGained.roundToInt()
}

private class PotencyContext(
    val context: SkillApplierHelper.TargetEvaluatorContext,
    val ftpScope: TpScalingContext,
)

private fun interface PotencyFn {
    fun invoke(context: PotencyContext): Float
}

fun getActorSkillCost(actorState: ActorState?, skillId: SkillId): V0AbilityCost {
    val baseCost = TacticsSkills[skillId].cost
    return (actorState?.behaviorController as? TacticsBehaviorController)?.getSkillCostOverride(skillId) ?: baseCost
}